import { useEffect, useState } from 'react';
import { api, formatErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Coffee, Save } from 'lucide-react';

export default function BreakWindowSettings() {
  const [form, setForm] = useState({ enabled: true, start: '13:00', end: '14:00' });
  const [activeNow, setActiveNow] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/settings/break-window');
      setForm({ enabled: !!data.enabled, start: data.start || '13:00', end: data.end || '14:00' });
      setActiveNow(!!data.active_now);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/settings/break-window', form);
      setActiveNow(!!data.active_now);
      toast.success('Break window updated');
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="tactical-card p-4 mb-6" data-testid="break-window-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-md grid place-items-center ${activeNow ? 'bg-violet-500/15 text-violet-500 animate-pulse' : 'bg-muted text-muted-foreground'}`}>
            <Coffee className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-lg font-black tracking-tight">Lunch break window</div>
            <div className="text-xs text-muted-foreground">Everyone shows as <span className="font-black text-violet-500">On Break</span> during this window. Offline alerts & logs are paused.</div>
          </div>
        </div>
        {activeNow && <span data-testid="break-active-badge" className="text-[10px] uppercase tracking-widest font-black text-violet-500 bg-violet-500/10 border border-violet-500/30 px-2 py-1 rounded-md">Break active now</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 items-end">
        <div className="flex items-center gap-2">
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm(f => ({ ...f, enabled: v }))} data-testid="break-enabled" id="bw-enabled" />
          <Label htmlFor="bw-enabled" className="text-sm">Enabled</Label>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest">Start (IST)</Label>
          <Input type="time" value={form.start} onChange={(e) => setForm(f => ({ ...f, start: e.target.value }))} data-testid="break-start" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest">End (IST)</Label>
          <Input type="time" value={form.end} onChange={(e) => setForm(f => ({ ...f, end: e.target.value }))} data-testid="break-end" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="break-save" className="bg-primary hover:bg-primary/90 text-white">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save window'}
        </Button>
      </div>
    </div>
  );
}
